import { useState, useEffect, useCallback } from 'react'
import { C, font, btnSm } from '../design'
import { runApplyQueue, getApplyQueue, updateApplyQueueItem, deleteApplyQueueItem, pollThreadsJob } from '../utils/api'

const SITE = 'https://ai-digital-twin.ru'

const GROUPS = [
  { key: 'todo',   label: 'К отправке', statuses: ['ready', 'opened'] },
  { key: 'sent',   label: 'Отправлено', statuses: ['sent'] },
  { key: 'answer', label: 'Ответы',     statuses: ['invited', 'rejected'] },
  { key: 'skip',   label: 'Пропущено',  statuses: ['skipped', 'dismissed'] },
]

const STATUS_LABEL = {
  ready: 'готов', opened: 'открыт на HH', sent: 'отправлен', invited: 'приглашение',
  rejected: 'отказ', skipped: 'не подходит', dismissed: 'пропущен',
}

function pill(color) {
  return {
    ...btnSm, fontFamily: font.mono, fontSize: 10, background: 'transparent',
    border: `1px solid ${color}55`, color,
  }
}

export default function ApplyQueue({ userId, roles }) {
  const [items, setItems] = useState(null)
  const [spend, setSpend] = useState(null)
  const [group, setGroup] = useState('todo')
  const [running, setRunning] = useState(false)
  const [progress, setProgress] = useState(null)
  const [error, setError] = useState(null)
  const [copiedId, setCopiedId] = useState(null)
  const [openLetter, setOpenLetter] = useState(null)

  const load = useCallback(async () => {
    try {
      const d = await getApplyQueue(userId)
      setItems(d.items)
      setSpend({ today: d.spent_today, cap: d.daily_cap })
    } catch (e) {
      setError(e.message)
    }
  }, [userId])

  useEffect(() => { load() }, [load])

  const topRoles = (roles || []).filter(r => r.hh_query || r.title).slice(0, 4)

  async function startRun() {
    setError(null); setRunning(true); setProgress({ phase: 'search' })
    try {
      const jobId = await runApplyQueue(userId, topRoles)
      const result = await pollThreadsJob(jobId, j => setProgress(j), 20 * 60 * 1000)
      setProgress({ phase: 'done', ...result })
      await load()
    } catch (e) {
      setError(e.message)
    } finally {
      setRunning(false)
    }
  }

  async function patch(item, status) {
    setItems(prev => prev.map(x => x.id === item.id ? { ...x, status } : x))
    try { await updateApplyQueueItem(item.id, { status }) } catch (e) { setError(e.message); load() }
  }

  async function remove(item) {
    setItems(prev => prev.filter(x => x.id !== item.id))
    try { await deleteApplyQueueItem(item.id) } catch (e) { setError(e.message); load() }
  }

  // Один клик: письмо (+ссылка на страницу под вакансию) в буфер, вакансия открывается на HH
  async function apply(item) {
    const link = item.slug ? `\n\nПодробнее под вашу вакансию: ${SITE}/r/${item.slug}` : ''
    try { await navigator.clipboard.writeText((item.letter || '') + link) } catch {}
    setCopiedId(item.id)
    window.open(`https://hh.ru/vacancy/${item.vacancy_id}`, '_blank', 'noopener')
    if (item.status === 'ready') patch(item, 'opened')
  }

  const counts = Object.fromEntries(GROUPS.map(g => [g.key, (items || []).filter(i => g.statuses.includes(i.status)).length]))
  const visible = (items || []).filter(i => GROUPS.find(g => g.key === group).statuses.includes(i.status))

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
        <div style={{ fontFamily: font.body, fontSize: 12, color: C.textMuted, lineHeight: 1.6, maxWidth: 520 }}>
          Система сама подберёт вакансии под лучшие роли, отсеет неподходящие и подготовит письмо со страницей под каждую.
          Вам остаётся нажать «Откликнуться»: письмо окажется в буфере, вакансия откроется на HH — вставьте письмо и отправьте.
        </div>
        <button
          onClick={startRun}
          disabled={running || !topRoles.length}
          style={{ ...btnSm, padding: '9px 20px', border: `1px solid ${C.borderHi}`, background: 'rgba(184,189,212,0.08)', color: C.accent, opacity: running || !topRoles.length ? 0.5 : 1 }}
        >
          {running ? 'Готовлю…' : 'Подобрать отклики'}
        </button>
      </div>

      {!topRoles.length && (
        <div style={{ fontFamily: font.body, fontSize: 12, color: C.orange, marginBottom: 16 }}>
          Сначала откройте «Навигатор» — роли ещё не подобраны.
        </div>
      )}

      {running && progress && (
        <div style={{ fontFamily: font.mono, fontSize: 10, color: C.textMuted, letterSpacing: '0.06em', marginBottom: 16 }}>
          {progress.phase === 'search' ? 'Ищу вакансии и отсеиваю неподходящие…' : `Готовлю пакеты: ${progress.done ?? 0} из ${progress.total ?? '…'}`}
        </div>
      )}
      {!running && progress?.phase === 'done' && (
        <div style={{ fontFamily: font.mono, fontSize: 10, color: C.green, letterSpacing: '0.06em', marginBottom: 16 }}>
          ✓ Подготовлено {progress.prepared ?? 0}{progress.skipped ? ` · отсеяно ${progress.skipped}` : ''}{progress.failed ? ` · ошибок ${progress.failed}` : ''}{progress.spent != null ? ` · потрачено $${Number(progress.spent).toFixed(2)}` : ''}{progress.stopped ? ' · остановлено по лимиту' : ''}
        </div>
      )}
      {spend && (
        <div style={{ fontFamily: font.mono, fontSize: 9, color: spend.today >= spend.cap ? C.red : C.textMuted, letterSpacing: '0.08em', marginBottom: 12 }}>
          потрачено сегодня ${Number(spend.today).toFixed(2)} из ${spend.cap}
        </div>
      )}
      {error && <div style={{ fontFamily: font.body, fontSize: 12, color: C.red, marginBottom: 16 }}>{error}</div>}

      <div style={{ display: 'flex', gap: 0, borderBottom: `1px solid ${C.border}`, marginBottom: 20, overflowX: 'auto' }}>
        {GROUPS.map(g => (
          <button
            key={g.key}
            onClick={() => setGroup(g.key)}
            style={{
              fontFamily: font.mono, fontSize: 10, letterSpacing: '0.08em', padding: '8px 16px', background: 'transparent',
              border: 'none', cursor: 'pointer', whiteSpace: 'nowrap', marginBottom: -1,
              color: group === g.key ? C.text : C.textMuted,
              borderBottom: group === g.key ? `2px solid ${C.accent}` : '2px solid transparent',
            }}
          >
            {g.label} · {counts[g.key] ?? 0}
          </button>
        ))}
      </div>

      {items === null && !error && <div style={{ fontFamily: font.mono, fontSize: 10, color: C.textMuted }}>Загружаю…</div>}
      {items && !visible.length && (
        <div style={{ fontFamily: font.body, fontSize: 12, color: C.textMuted }}>Здесь пока пусто.</div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {visible.map(item => {
          const v = item.vacancy || {}
          const isTodo = item.status === 'ready' || item.status === 'opened'
          return (
            <div key={item.id} style={{ padding: '16px 18px', borderRadius: 10, border: `1px solid ${C.border}`, background: C.surface }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontFamily: font.body, fontSize: 14, color: C.text, fontWeight: 500 }}>{v.title}</div>
                  <div style={{ fontFamily: font.body, fontSize: 12, color: C.textMuted, marginTop: 3 }}>
                    {v.company}{v.city ? ` · ${v.city}` : ''}{v.remote ? ' · удалённо' : ''}{v.salary ? ` · ${v.salary}` : ''}
                  </div>
                </div>
                <div style={{ fontFamily: font.mono, fontSize: 9, color: C.textMuted, letterSpacing: '0.08em', textAlign: 'right' }}>
                  <div>{STATUS_LABEL[item.status]}</div>
                  {item.coverage != null && <div style={{ color: C.green, marginTop: 3 }}>покрытие {item.coverage}%</div>}
                </div>
              </div>

              {item.status === 'skipped' && (
                <div style={{ fontFamily: font.body, fontSize: 12, color: C.textMuted, marginTop: 8, lineHeight: 1.5 }}>
                  {(item.reasons || []).map((r, i) => <div key={i}>· {r.text}</div>)}
                </div>
              )}

              {item.letter && item.status !== 'skipped' && (
                <div style={{ marginTop: 10 }}>
                  <div
                    onClick={() => setOpenLetter(openLetter === item.id ? null : item.id)}
                    style={{ fontFamily: font.body, fontSize: 12, color: C.textMuted, lineHeight: 1.6, whiteSpace: 'pre-wrap', cursor: 'pointer' }}
                  >
                    {openLetter === item.id ? item.letter : `${item.letter.slice(0, 170)}…`}
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12, alignItems: 'center' }}>
                {isTodo && (
                  <button onClick={() => apply(item)} style={{ ...btnSm, padding: '7px 18px', border: `1px solid ${C.accent}66`, background: 'rgba(184,189,212,0.1)', color: C.accent }}>
                    Откликнуться →
                  </button>
                )}
                {isTodo && item.status === 'opened' && (
                  <>
                    <button onClick={() => patch(item, 'sent')} style={pill(C.green)}>✓ Отправил</button>
                  </>
                )}
                {isTodo && <button onClick={() => patch(item, 'dismissed')} style={pill(C.textMuted)}>Пропустить</button>}
                {item.status === 'sent' && (
                  <>
                    <button onClick={() => patch(item, 'invited')} style={pill(C.green)}>Пригласили</button>
                    <button onClick={() => patch(item, 'rejected')} style={pill(C.red)}>Отказ</button>
                  </>
                )}
                {(item.status === 'dismissed' || item.status === 'skipped') && (
                  <button onClick={() => remove(item)} style={pill(C.textMuted)}>Убрать</button>
                )}
                {item.slug && (
                  <a href={`/r/${item.slug}`} target="_blank" rel="noreferrer" style={{ fontFamily: font.mono, fontSize: 9, color: C.textMuted, textDecoration: 'none', letterSpacing: '0.06em' }}>
                    страница для HR ↗
                  </a>
                )}
                {copiedId === item.id && isTodo && (
                  <span style={{ fontFamily: font.mono, fontSize: 10, color: C.green }}>✓ письмо в буфере — вставьте на HH (⌘V)</span>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
